import { iconColorLeaseComp } from "@/constants/mapStyles";
import { hasAddressLine2AsCustom } from "@/shared/utils";
import { EmeraldIcon } from "@emerald-react/icon";
import { useSurvey } from "@hooks/useSurveySelectors";
import { getIconData } from "@shared/utils/map";
import type { LeaseComp } from "@survey-types/survey.leaseComp.types";
import type { Property } from "@survey-types/survey.property.types";
import type { ReactElement } from "react";
import { DetailedSurvey } from "../../types/survey.core.types";

interface MarketMapLegendsProps {
  mode?: string;
  pageProperties?: Property[];
  startIndexFrom?: number;
  pageLeaseComps?: LeaseComp[];
  startLeaseIndexFrom?: number;
}

interface PropertyIndexMap {
  [propertyId: string]: number | string | ReactElement;
}

const MarketMapLegends = ({
  mode,
  pageProperties,
  startIndexFrom = 0,
  pageLeaseComps = [],
  startLeaseIndexFrom = 0,
}: MarketMapLegendsProps) => {
  const survey = useSurvey();
  const isAddressLine2AsCustom = hasAddressLine2AsCustom(
    survey as DetailedSurvey,
  );
  // Get legend display fields from survey
  const legendFields = survey?.customLegends?.displayFields || {
    hasIcon: true,
    hasPropertyName: true,
    hasAddressLine1: true,
    hasAddressLine2: !isAddressLine2AsCustom,
    hasCity: true,
    hasState: true,
    hasZipCode: true,
    hasGroupName: false,
    hasGroupIconOnlyAvailable: false,
  };

  const properties = survey?.properties || [];

  // For legends, show all non-hidden properties regardless of shortlisting or current location settings
  const visibleProperties = pageProperties
    ? pageProperties
    : properties.filter((property) => !property.isHidden);

  // Create property index mapping
  let index = startIndexFrom;
  const propertyIndexMap: PropertyIndexMap = visibleProperties.reduce(
    (acc: PropertyIndexMap, property: Property) => {
      if (property.isCurrentLocation) {
        const iconColor = getIconData(property).iconColor;
        return {
          ...acc,
          [property.propertyId]: (
            <div
              className="w-5 h-5 flex items-center justify-center"
              style={{ color: iconColor }}
            >
              <EmeraldIcon icon="stars" type="filled" variant="icon" />
            </div>
          ),
        };
      }
      const currentIndex = index + 1;
      index = currentIndex;
      return { ...acc, [property.propertyId]: currentIndex };
    },
    {} as PropertyIndexMap,
  );

  // Format address line with proper commas
  const formatAddress = (property: Property): string => {
    const parts: string[] = [];
    if (legendFields.hasAddressLine1 && property.address1) {
      parts.push(property.address1);
    }
    const address2 = property.address2 === "—" ? "" : property.address2;
    if (legendFields.hasAddressLine2 && address2) {
      if (parts.length > 0) {
        parts[parts.length - 1] = `${parts[parts.length - 1]}\n${address2}`;
      } else {
        parts.push(address2);
      }
    }

    const cityStateZip: string[] = [];
    if (legendFields.hasCity && property.city) {
      cityStateZip.push(property.city);
    }
    if (legendFields.hasState && property.stateProvince) {
      cityStateZip.push(property.stateProvince);
    }
    if (legendFields.hasZipCode && property.postalCode) {
      cityStateZip.push(property.postalCode);
    }
    if (cityStateZip.length > 0) {
      if (address2 === "" && legendFields.hasAddressLine2 && parts.length > 0) {
        parts[parts.length - 1] =
          `${parts[parts.length - 1]}\n${cityStateZip.join(", ")}`;
      } else {
        parts.push(cityStateZip.join(", "));
      }
    }
    return parts.join(", ");
  };

  // Render individual property legend item
  const renderPropertyLegendItem = (property: Property) => {
    // Define grid template: icon column (auto-width) + property info column (1fr)
    const formattedAddress = formatAddress(property);
    const hasSecondAddressLine = Boolean(
      legendFields.hasAddressLine2 && property.address2,
    );

    return (
      <div
        key={property.propertyId}
        className="grid gap-3 items-start grid-cols-[auto_1fr]"
      >
        {/* Property Index/Icon - conditional styling based on current location */}
        <div className="flex items-center justify-center shrink-0">
          {property.isCurrentLocation ? (
            // Current location already has its styling from propertyIndexMap
            propertyIndexMap[property.propertyId]
          ) : (
            // Regular properties get colored background - use primary color if shortlisted
            <div
              className="flex items-center justify-center w-5 h-5 rounded-full text-xs text-white"
              style={{
                backgroundColor: getIconData(property).iconColor,
              }}
            >
              {propertyIndexMap[property.propertyId]}
            </div>
          )}
        </div>

        {/* Property Information */}
        <div className="overflow-hidden flex flex-col">
          {legendFields.hasPropertyName && (
            <div
              className="text-sm truncate leading-snug"
              title={property.name || property.address1 || ""}
            >
              {property.name || property.address1 || ""}
            </div>
          )}

          {formattedAddress && (
            <div
              className={`text-sm leading-snug ${
                hasSecondAddressLine ? "whitespace-pre-line" : "truncate"
              }`}
              style={
                hasSecondAddressLine
                  ? {
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }
                  : undefined
              }
              title={formattedAddress}
            >
              {formattedAddress}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Split properties for portrait layout (two columns)
  const isPortrait =
    mode === "FlyerPreview" || survey?.pdfPaperSize === "Portrait";

  // Visible (non-hidden) lease comps — sliced and passed in by MarketMapWrapper
  const visibleLeaseComps = pageLeaseComps;

  // Render a single lease comp legend item (diamond marker + name/address)
  const renderLeaseCompLegendItem = (leaseComp: LeaseComp, idx: number) => {
    const fields = leaseComp.leaseCompFields;

    const addressParts: string[] = [];
    if (legendFields.hasAddressLine1 && fields?.propertyAddress)
      addressParts.push(fields.propertyAddress);
    if (legendFields.hasAddressLine2 && fields?.propertyAddress2)
      addressParts.push(fields.propertyAddress2);
    if (legendFields.hasCity && fields?.propertyCity)
      addressParts.push(fields.propertyCity);
    const stateZip: string[] = [];
    if (legendFields.hasState && fields?.propertyState)
      stateZip.push(fields.propertyState);
    if (legendFields.hasZipCode && fields?.propertyPostalCode)
      stateZip.push(fields.propertyPostalCode);
    if (stateZip.length > 0) addressParts.push(stateZip.join(" "));
    const address = addressParts.join(", ");

    return (
      <div
        key={leaseComp.leaseCompId}
        className="grid gap-3 items-start grid-cols-[auto_1fr]"
      >
        {/* Square marker */}
        <div
          className="flex items-center justify-center shrink-0 w-5 h-5 border border-white p-0.5 text-xs font-normal leading-4 tracking-[0.005em] text-white"
          style={{
            backgroundColor: leaseComp.mapData?.iconColor || iconColorLeaseComp,
          }}
        >
          {startLeaseIndexFrom + idx + 1}
        </div>

        {/* Lease Comp Information */}
        <div className="overflow-hidden flex flex-col">
          {legendFields.hasPropertyName && (
            <div
              className="text-sm truncate leading-snug"
              title={leaseComp.name || ""}
            >
              {leaseComp.name || ""}
            </div>
          )}
          {address && (
            <div className="text-sm truncate leading-snug" title={address}>
              {address}
            </div>
          )}
        </div>
      </div>
    );
  };

  const hasContent =
    visibleProperties.length > 0 || visibleLeaseComps.length > 0;

  // Combine all items into a single ordered list for a unified 2-column layout
  const allItems: Array<
    | { kind: "property"; item: Property }
    | { kind: "lease"; item: LeaseComp; leaseIndex: number }
  > = [
    ...visibleProperties.map((p) => ({ kind: "property" as const, item: p })),
    ...visibleLeaseComps.map((lc, i) => ({
      kind: "lease" as const,
      item: lc,
      leaseIndex: i,
    })),
  ];

  if (!survey || !hasContent) {
    return null;
  }

  const midPoint = Math.ceil(allItems.length / 2);
  const leftItems = allItems.slice(0, midPoint);
  const rightItems = allItems.slice(midPoint);

  const renderItem = (
    entry:
      | { kind: "property"; item: Property }
      | { kind: "lease"; item: LeaseComp; leaseIndex: number },
  ) =>
    entry.kind === "property"
      ? renderPropertyLegendItem(entry.item)
      : renderLeaseCompLegendItem(entry.item, entry.leaseIndex);

  return (
    <>
      {isPortrait ? (
        // Portrait Layout - first half in left column, second half in right column
        <div className="grid grid-cols-2 gap-4 p-4 place-content-start">
          <div className="grid gap-y-4 place-content-start">
            {leftItems.map(renderItem)}
          </div>
          <div className="grid gap-y-4 place-content-start">
            {rightItems.map(renderItem)}
          </div>
        </div>
      ) : (
        // Landscape Layout - Single Column
        <div className="gap-y-2 p-2 grid place-content-start">
          {visibleProperties.map((property) =>
            renderPropertyLegendItem(property),
          )}
          {visibleLeaseComps.map((lc, i) => renderLeaseCompLegendItem(lc, i))}
        </div>
      )}
    </>
  );
};

export default MarketMapLegends;
